import { useCallback } from 'react';

import {
  CreatePackageComponent,
  PackageComponentSchema,
  PackageRequestInfo,
  ReorderComponentsRequest,
  UpdatePackageComponent,
  useAddComponentMutation,
  useRemoveComponentMutation,
  useReorderComponentsMutation,
  useUpdateComponentMutation,
} from '@/services/api/hipaaPackageService';

interface UsePackageComponentsProps {
  packageId: string;
  requestInfo: PackageRequestInfo;
  components: PackageComponentSchema[];
  onRefetch: () => Promise<void>;
}

/**
 * Hook for managing package component mutations
 * Components data is provided by parent (from package query)
 * Mutations trigger parent refetch to keep data in sync
 */
export const usePackageComponents = ({
  packageId,
  requestInfo,
  components,
  onRefetch,
}: UsePackageComponentsProps) => {
  // Mutation hooks
  const [addComponent, { isLoading: isAdding }] = useAddComponentMutation();
  const [removeComponent, { isLoading: isRemoving }] =
    useRemoveComponentMutation();
  const [updateComponent, { isLoading: isUpdating }] =
    useUpdateComponentMutation();
  const [reorderComponents, { isLoading: isReordering }] =
    useReorderComponentsMutation();

  // Add a template component
  const addTemplate = useCallback(
    async (templateId: string, overrides?: Record<string, string>) => {
      if (!packageId) return;

      const component: CreatePackageComponent = {
        component_type: 'template',
        v2_template_id: templateId,
        order: components?.length || 0,
        ...(overrides && { overrides }),
      };

      await addComponent({
        ...requestInfo,
        packageId,
        createPackageComponent: component,
      }).unwrap();
      await onRefetch();
    },
    [addComponent, packageId, components, requestInfo, onRefetch],
  );

  // Add a blob/document component
  const addBlob = useCallback(
    async (blobId: string) => {
      if (!packageId) return;

      const component: CreatePackageComponent = {
        component_type: 'matter_blob',
        blob_id: blobId,
        order: components?.length || 0,
      };

      await addComponent({
        ...requestInfo,
        packageId,
        createPackageComponent: component,
      }).unwrap();
      await onRefetch();
    },
    [addComponent, packageId, components, requestInfo, onRefetch],
  );

  // Add a static document component
  const addStaticDocument = useCallback(
    async (
      tag:
        | 'client_id'
        | 'attorney_relationship'
        | 'reproductive_rights'
        | 'texas_affidavit',
    ) => {
      if (!packageId) return;

      const component: CreatePackageComponent = {
        component_type: 'static_document',
        static_document_tag: tag,
        order: components?.length || 0,
      };

      await addComponent({
        ...requestInfo,
        packageId,
        createPackageComponent: component,
      }).unwrap();
      await onRefetch();
    },
    [addComponent, packageId, components, requestInfo, onRefetch],
  );

  const addSignedAuthorizationComponent = useCallback(
    async (templateId: string, signedBlobId: string) => {
      if (!packageId) return;

      const component: CreatePackageComponent = {
        component_type: 'signed_authorization_merge',
        v2_template_id: templateId,
        signed_blob_id: signedBlobId,
        order: components?.length || 0,
      };

      await addComponent({
        ...requestInfo,
        packageId,
        createPackageComponent: component,
      }).unwrap();
      await onRefetch();
    },
    [addComponent, packageId, components, requestInfo, onRefetch],
  );

  // Remove a component
  const removeComponentById = useCallback(
    async (componentId: string) => {
      await removeComponent({
        ...requestInfo,
        packageId,
        id: componentId,
      }).unwrap();
      await onRefetch();
    },
    [removeComponent, requestInfo, packageId, onRefetch],
  );

  // Update component overrides
  const updateComponentOverrides = useCallback(
    async (componentId: string, overrides: Record<string, string>) => {
      const data: UpdatePackageComponent = {
        overrides,
      };

      await updateComponent({
        ...requestInfo,
        packageId,
        id: componentId,
        patchedUpdatePackageComponent: data,
      }).unwrap();
      await onRefetch();
    },
    [updateComponent, requestInfo, packageId, onRefetch],
  );

  // Reorder components
  const reorderComponentList = useCallback(
    async (componentIds: string[]) => {
      const request: ReorderComponentsRequest = {
        components: componentIds.map((id, index) => ({
          component_id: id,
          order: index,
        })),
      };

      await reorderComponents({
        ...requestInfo,
        packageId,
        reorderComponentsRequest: request,
      }).unwrap();
      await onRefetch();
    },
    [reorderComponents, requestInfo, packageId, onRefetch],
  );

  return {
    isLoading: isAdding || isRemoving || isUpdating || isReordering,
    addTemplate,
    addBlob,
    addStaticDocument,
    addSignedAuthorizationComponent,
    removeComponent: removeComponentById,
    updateComponentOverrides,
    reorderComponents: reorderComponentList,
  };
};
